/**
 * Hook to discover workflow variables from node definitions.
 *
 * @module FlowDesigner/hooks/useWorkflowVariables
 *
 * Scans all workflow nodes for:
 * - $var.{name} references in inputValues, customActionTitle, actionDescription
 * - dataRequirements names (become variables when values are provided)
 * - $MACRO() expressions in inputValues (produce variables at runtime)
 */

import { useMemo } from 'react';
import type { WorkflowNode } from '../../../../types/workflow';
import type { VariableSource, ApiSequenceExport, WorkflowInputDefinition } from '@chatsdet/types';
import { slugifySeedName, resolveNodeOutputs } from '@chatsdet/types';
import type { TransitiveSeedOutput } from './useSeedNodeOutputs';
import { nodeLabel } from '../utils/nodeLabel';
import {
  scanStringForVarRefs,
  scanStringForMacros,
  MACRO_TO_VAR_KEY,
} from '../validation/variableScanning';

export interface VariableDefiner {
  nodeId: string;
  stepIndex: number;
  stepLabel: string;
}

export interface WorkflowVariable {
  name: string;
  source: VariableSource;
  referencedByNodeIds: string[];
  definedByNodeId: string | null;
  definedByStepIndex: number | null;
  definedByStepLabel: string | null;
  hasDesignTimeValue: boolean;
  /** The template/expression value at design time (e.g. "Acme Test - $RANDOM_STRING(6)") */
  designTimeValue: string | null;
  /** Author-supplied description (e.g. an env var's declared description). */
  description: string | null;
  /** All nodes that define this variable (for collision detection) */
  allDefiners: VariableDefiner[];
  /** True when multiple steps define the same variable name */
  hasCollision: boolean;
}

type SeedOutputEntry = {
  dataKey: string;
  name: string;
  credentialOutput?: {
    fieldMapping: Record<string, string>;
  };
};

/** A property-declared environment variable, injected at run time as $var.ENV.<name>. */
export type EnvVarDefinition = {
  name: string;
  description?: string;
  sensitive?: boolean;
};

export function useWorkflowVariables(
  nodes: WorkflowNode[],
  seedOutputsByWorkflowId?: Map<string, SeedOutputEntry[]>,
  selfSeedOutputs?: SeedOutputEntry[],
  transitiveSeedOutputs?: Map<string, TransitiveSeedOutput>,
  workflowInputs?: WorkflowInputDefinition[],
  envVarDefinitions?: EnvVarDefinition[],
  callFlowOutputsByFlowId?: Map<string, SeedOutputEntry[]>
): WorkflowVariable[] {
  return useMemo(() => {
    const variableMap = new Map<string, {
      source: VariableSource;
      referencedByNodeIds: Set<string>;
      definedByNodeId: string | null;
      definerNodeIds: Set<string>;
      hasDesignTimeValue: boolean;
      designTimeValue: string | null;
      description: string | null;
    }>();

    const getOrCreate = (name: string, source: VariableSource) => {
      let entry = variableMap.get(name);
      if (!entry) {
        entry = {
          source,
          referencedByNodeIds: new Set(),
          definedByNodeId: null,
          definerNodeIds: new Set(),
          hasDesignTimeValue: false,
          designTimeValue: null,
          description: null,
        };
        variableMap.set(name, entry);
      } else if (entry.source === 'unknown' && source !== 'unknown') {
        // Upgrade from unknown (reference-only) to a concrete source
        entry.source = source;
      }
      return entry;
    };

    const trackDefiner = (entry: ReturnType<typeof getOrCreate>, nodeId: string) => {
      entry.definedByNodeId = entry.definedByNodeId ?? nodeId;
      entry.definerNodeIds.add(nodeId);
    };

    // Register workflow-level inputs (before node scan so they appear first)
    if (workflowInputs) {
      for (const input of workflowInputs) {
        if (input.kind === 'value') {
          const entry = getOrCreate(input.name, 'workflowInput');
          entry.hasDesignTimeValue = !!input.defaultValue;
          entry.designTimeValue = input.defaultValue ?? null;
        }
        if (input.kind === 'credential') {
          for (const [field, enabled] of Object.entries(input.credentialFields)) {
            if (!enabled) continue;
            const credEntry = getOrCreate(`credential.${field}`, 'credential');
            credEntry.hasDesignTimeValue = false;

            const nsEntry = getOrCreate(`${input.name}.${field}`, 'credential');
            nsEntry.hasDesignTimeValue = false;
          }
        }
      }
    }

    // Register property-declared environment variables. These are injected at
    // run time as $var.ENV.<name> (see initialize-segment.ts), so they are keyed
    // under the `ENV.` namespace to match the inserted token.
    if (envVarDefinitions) {
      for (const def of envVarDefinitions) {
        if (!def.name) continue;
        const entry = getOrCreate(`ENV.${def.name}`, 'env');
        entry.hasDesignTimeValue = false; // resolved per-environment at run time
        entry.description = def.description?.trim() || null;
      }
    }

    for (const node of nodes) {
      const data = node.data as Record<string, unknown> | undefined;
      if (!data) continue;

      // Scan text fields for $var.{name} references
      const textFields = [
        data.customActionTitle,
        data.actionDescription,
        data.condition,
        data.conditionDescription,
        data.pageUrl,
      ].filter((v): v is string => typeof v === 'string');

      // dataRequirements define which inputValues are real variables.
      // Orphaned inputValues (no matching dataRequirement) are ghost data from
      // previous agent runs and should not be exposed as variables — matching
      // backend behavior in step-input-resolver.ts and extractDefinedVariablesFromConfig.
      const inputValues = data.inputValues as Record<string, string> | undefined;
      const dataRequirements = data.dataRequirements as Array<{ name: string }> | undefined;
      const dataReqNames = new Set(
        (Array.isArray(dataRequirements) ? dataRequirements : [])
          .map((r) => r.name)
          .filter((n): n is string => typeof n === 'string')
      );

      // Only register inputValues that are backed by a dataRequirement.
      // When no dataRequirements exist, skip all inputValues (they're ghost data).
      if (inputValues && dataReqNames.size > 0) {
        for (const [fieldName, fieldValue] of Object.entries(inputValues)) {
          if (typeof fieldValue !== 'string') continue;
          if (!dataReqNames.has(fieldName)) {
            // Still scan ghost values for $var. references so references are tracked
            textFields.push(fieldValue);
            continue;
          }
          textFields.push(fieldValue);

          // inputValues themselves define variables (source: inputValue)
          const entry = getOrCreate(fieldName, 'inputValue');
          trackDefiner(entry, node.id);
          entry.hasDesignTimeValue = true;
          entry.designTimeValue = fieldValue;

          // Check if value contains macros → those produce variables too
          // Namespace under the parent field name (e.g. sensor_name_random_string)
          const macroNames = scanStringForMacros(fieldValue);
          for (const macroName of macroNames) {
            const macroSuffix = MACRO_TO_VAR_KEY[macroName];
            if (macroSuffix) {
              const varKey = `${fieldName}_${macroSuffix}`;
              const macroEntry = getOrCreate(varKey, 'macro');
              trackDefiner(macroEntry, node.id);
              macroEntry.hasDesignTimeValue = true;
              // Extract just the macro expression (e.g. "$RANDOM_STRING(6)") from the template
              const exprPattern = new RegExp(`\\$${macroName}\\([^)]*\\)`);
              const exprMatch = fieldValue.match(exprPattern);
              macroEntry.designTimeValue = exprMatch?.[0] ?? `$${macroName}()`;
            }
          }
        }
      } else if (inputValues) {
        // No dataRequirements — still scan values for $var. references
        for (const fieldValue of Object.values(inputValues)) {
          if (typeof fieldValue === 'string') textFields.push(fieldValue);
        }
      }
      if (Array.isArray(dataRequirements)) {
        for (const req of dataRequirements) {
          if (typeof req.name === 'string') {
            const entry = getOrCreate(req.name, 'inputValue');
            trackDefiner(entry, node.id);
            // Has design-time value only if there's a matching inputValue
            if (inputValues && req.name in inputValues) {
              entry.hasDesignTimeValue = true;
            }
          }
        }
      }

      // Per-node-type outputs (e.g. notification.to/subject/body).
      // Single source of truth lives in `@chatsdet/types/node-outputs`.
      for (const output of resolveNodeOutputs(node.type, data)) {
        const entry = getOrCreate(output.name, output.source);
        trackDefiner(entry, node.id);
        if (output.designTimeValue !== undefined) {
          entry.hasDesignTimeValue = true;
          entry.designTimeValue = output.designTimeValue;
        }
      }

      // API sequence exports define extracted variables
      if (node.type === 'api_sequence') {
        const exports = data.exports as ApiSequenceExport[] | undefined;
        if (Array.isArray(exports)) {
          for (const exp of exports) {
            if (typeof exp.variableName === 'string' && exp.variableName) {
              const entry = getOrCreate(exp.variableName, 'extracted');
              trackDefiner(entry, node.id);
            }
          }
        }
      }

      // Collect all $var.{name} references from text fields
      for (const text of textFields) {
        const varRefs = scanStringForVarRefs(text);
        for (const varName of varRefs) {
          const entry = getOrCreate(varName, 'unknown');
          entry.referencedByNodeIds.add(node.id);
        }
      }
    }

    // Inject variables from seed node outputs
    if (seedOutputsByWorkflowId && seedOutputsByWorkflowId.size > 0) {
      for (const node of nodes) {
        if (node.type !== 'seed') continue;
        const data = node.data as Record<string, unknown> | undefined;
        const seedWorkflowId = data?.seedWorkflowId as string | undefined;
        if (!seedWorkflowId) continue;

        const outputs = seedOutputsByWorkflowId.get(seedWorkflowId);
        if (!outputs) continue;

        for (const output of outputs) {
          if (!output.dataKey) continue;
          const entry = getOrCreate(output.dataKey, 'seed');
          trackDefiner(entry, node.id);
          entry.hasDesignTimeValue = false; // populated at runtime

          // Emit credential field variables (e.g., credential.username, {dataKey}.username)
          if (output.credentialOutput?.fieldMapping) {
            for (const field of Object.keys(output.credentialOutput.fieldMapping)) {
              const credEntry = getOrCreate(`credential.${field}`, 'credential');
              trackDefiner(credEntry, node.id);
              credEntry.hasDesignTimeValue = false;

              const dataKeyEntry = getOrCreate(`${output.dataKey}.${field}`, 'credential');
              trackDefiner(dataKeyEntry, node.id);
              dataKeyEntry.hasDesignTimeValue = false;
            }
          }
        }
      }
    }

    // Inject variables from call_flow node outputs (declared outputs of the
    // called flow, published back with source 'childFlowOutput' — distinct
    // from 'seed' since a called flow is not a seed script).
    if (callFlowOutputsByFlowId && callFlowOutputsByFlowId.size > 0) {
      for (const node of nodes) {
        if (node.type !== 'call_flow') continue;
        const data = node.data as Record<string, unknown> | undefined;
        const flowId = data?.flowId as string | undefined;
        if (!flowId) continue;

        const outputs = callFlowOutputsByFlowId.get(flowId);
        if (!outputs) continue;

        const flowName = typeof data?.flowName === 'string' ? data.flowName : null;

        for (const output of outputs) {
          if (!output.dataKey) continue;
          // Bare form: $var.child_status
          const entry = getOrCreate(output.dataKey, 'childFlowOutput');
          trackDefiner(entry, node.id);
          entry.hasDesignTimeValue = false; // populated at runtime

          // Namespaced form: $var.CALLFLOW_child.child_status. The handler
          // publishes via appendSeedVariables(published, childWorkflow.name, …),
          // which registers BOTH forms at run time — so omitting it here made a
          // valid reference read as an unknown variable in the panel.
          if (flowName) {
            const namespacedKey = `${slugifySeedName(flowName)}.${output.dataKey}`;
            const nsEntry = getOrCreate(namespacedKey, 'childFlowOutput');
            trackDefiner(nsEntry, node.id);
            nsEntry.hasDesignTimeValue = false;
          }
        }
      }
    }

    // Inject variables from transitive seed dependencies (both simple and namespaced forms)
    if (transitiveSeedOutputs && transitiveSeedOutputs.size > 0) {
      const directSeedNode = nodes.find((n) => n.type === 'seed');
      for (const [, { outputs, workflowName }] of transitiveSeedOutputs) {
        for (const output of outputs) {
          if (!output.dataKey) continue;

          // Simple form: $var.client_id (matches runtime behavior)
          const simpleEntry = getOrCreate(output.dataKey, 'seed');
          simpleEntry.hasDesignTimeValue = false;
          if (directSeedNode) trackDefiner(simpleEntry, directSeedNode.id);

          // Namespaced form: $var.Create_Client_Matter.client_id
          if (workflowName) {
            const namespacedKey = `${slugifySeedName(workflowName)}.${output.dataKey}`;
            const nsEntry = getOrCreate(namespacedKey, 'seed');
            nsEntry.hasDesignTimeValue = false;
            if (directSeedNode) trackDefiner(nsEntry, directSeedNode.id);
          }

          // Emit credential field variables (e.g., credential.username, {dataKey}.username)
          if (output.credentialOutput?.fieldMapping) {
            for (const field of Object.keys(output.credentialOutput.fieldMapping)) {
              const credEntry = getOrCreate(`credential.${field}`, 'credential');
              if (directSeedNode) trackDefiner(credEntry, directSeedNode.id);
              credEntry.hasDesignTimeValue = false;

              const dataKeyEntry = getOrCreate(`${output.dataKey}.${field}`, 'credential');
              if (directSeedNode) trackDefiner(dataKeyEntry, directSeedNode.id);
              dataKeyEntry.hasDesignTimeValue = false;
            }
          }
        }
      }
    }

    // Inject variables from the current workflow's own seed outputs.
    // For seed workflows: shows credential variables when editing the seed itself.
    // For teardown workflows: shows all parent seed outputs as available variables.
    if (selfSeedOutputs && selfSeedOutputs.length > 0) {
      for (const output of selfSeedOutputs) {
        // Always add the dataKey as a seed-sourced variable
        if (output.dataKey && !variableMap.has(output.dataKey)) {
          const entry = getOrCreate(output.dataKey, 'seed');
          entry.hasDesignTimeValue = false;
        }

        // Add credential field variables if this output has credential provisioning
        if (output.credentialOutput?.fieldMapping) {
          for (const field of Object.keys(output.credentialOutput.fieldMapping)) {
            const credName = `credential.${field}`;
            if (!variableMap.has(credName)) {
              const credEntry = getOrCreate(credName, 'credential');
              credEntry.hasDesignTimeValue = false;
            }

            if (output.dataKey) {
              const dataKeyName = `${output.dataKey}.${field}`;
              if (!variableMap.has(dataKeyName)) {
                const dataKeyEntry = getOrCreate(dataKeyName, 'credential');
                dataKeyEntry.hasDesignTimeValue = false;
              }
            }
          }
        }
      }
    }

    // Case D: Inject credential variables from login nodes that bind a stored credential.
    // Mirrors the runtime: handle-login-node.ts returns credentialId regardless of
    // `performLogin` (the skip-login path still binds the credential for downstream
    // $var.credential.* resolution — see step-by-step.ts's seedCredentialVariables call),
    // so this must NOT gate on performLogin, only on credentialId being set.
    for (const node of nodes) {
      if (node.type !== 'login') continue;
      const data = node.data as Record<string, unknown> | undefined;
      const credentialId = data?.credentialId;
      if (typeof credentialId !== 'string' || !credentialId) continue;

      for (const field of ['username', 'password'] as const) {
        const entry = getOrCreate(`credential.${field}`, 'credential');
        trackDefiner(entry, node.id);
        entry.hasDesignTimeValue = false;
      }
    }

    return Array.from(variableMap.entries())
      .map(([name, entry]) => {
        let definedByStepIndex: number | null = null;
        let definedByStepLabel: string | null = null;
        if (entry.definedByNodeId) {
          const idx = nodes.findIndex((n) => n.id === entry.definedByNodeId);
          if (idx !== -1) {
            definedByStepIndex = idx + 1;
            definedByStepLabel = nodeLabel(nodes, entry.definedByNodeId);
          }
        }

        // Build allDefiners from tracked node IDs
        const allDefiners: VariableDefiner[] = [];
        for (const defNodeId of entry.definerNodeIds) {
          const idx = nodes.findIndex((n) => n.id === defNodeId);
          if (idx !== -1) {
            allDefiners.push({
              nodeId: defNodeId,
              stepIndex: idx + 1,
              stepLabel: nodeLabel(nodes, defNodeId),
            });
          }
        }

        return {
          name,
          source: entry.source,
          referencedByNodeIds: Array.from(entry.referencedByNodeIds),
          definedByNodeId: entry.definedByNodeId,
          definedByStepIndex,
          definedByStepLabel,
          hasDesignTimeValue: entry.hasDesignTimeValue,
          designTimeValue: entry.designTimeValue,
          description: entry.description,
          allDefiners,
          hasCollision: allDefiners.length > 1,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    nodes,
    seedOutputsByWorkflowId,
    selfSeedOutputs,
    transitiveSeedOutputs,
    workflowInputs,
    envVarDefinitions,
    callFlowOutputsByFlowId,
  ]);
}
